import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { APP_SHORTCUT_EVENT } from "../utils/shortcuts";
import { resolveTaxLabelFromProfile } from "../utils/taxDetails";

const SIZE_OPTIONS = ["small", "medium", "large"];
const GST_OPTIONS = ["both", "separate", "none"];
const TAX_LABEL = resolveTaxLabelFromProfile(
  JSON.parse(localStorage.getItem("userData") || "{}")?.data || {}
);

const DEFAULTS = {
  printerWidth: "2in",
  printerFeedCount: "0",
  printLogoOnBill: true,
  printUpiQrOnBill: true,
  alwaysPrintUpiQr: false,
  printTokenNo: true,
  printGoogleReviewQr: false,
  boldRestaurantName: true,
  boldGrandTotal: true,
  logoSize: "medium",
  upiQrSize: "large",
  businessNameSize: "medium",
  totalAmountSize: "medium",
  savingAmountSize: "medium",
  googleReviewQrSize: "small",
  gstPrintFormat: "both",
  restaurantNameSize: "small",
  customerNameSize: "small",
};

const readBool = (key, fallback) => {
  const raw = localStorage.getItem(key);
  if (raw === null) return fallback;
  return raw === "true";
};

const readString = (key, fallback) => localStorage.getItem(key) || fallback;

const persistPrinterSettings = (settings) => {
  localStorage.setItem("printerWidth", settings.printerWidth);
  localStorage.setItem("printer_width", settings.printerWidth);
  localStorage.setItem("pos_printer_feed_count", settings.printerFeedCount);
  localStorage.setItem("printer_setting_printLogoOnBill", String(settings.printLogoOnBill));
  localStorage.setItem("printer_setting_printUpiQrOnBill", String(settings.printUpiQrOnBill));
  localStorage.setItem("printer_setting_alwaysPrintUpiQr", String(settings.alwaysPrintUpiQr));
  localStorage.setItem("printer_setting_printTokenNo", String(settings.printTokenNo));
  localStorage.setItem("printer_setting_printGoogleReviewQr", String(settings.printGoogleReviewQr));
  localStorage.setItem("printer_setting_boldRestaurantName", String(settings.boldRestaurantName));
  localStorage.setItem("printer_setting_boldGrandTotal", String(settings.boldGrandTotal));
  localStorage.setItem("printer_setting_logoSize", settings.logoSize);
  localStorage.setItem("printer_setting_upiQrSize", settings.upiQrSize);
  localStorage.setItem("printer_setting_businessNameSize", settings.businessNameSize);
  localStorage.setItem("printer_setting_totalAmountSize", settings.totalAmountSize);
  localStorage.setItem("printer_setting_savingAmountSize", settings.savingAmountSize);
  localStorage.setItem("printer_setting_googleReviewQrSize", settings.googleReviewQrSize);
  localStorage.setItem("printer_setting_gstPrintFormat", settings.gstPrintFormat);
  localStorage.setItem("pos_restaurant_name_size", settings.restaurantNameSize);
  localStorage.setItem("pos_customer_name_size", settings.customerNameSize);
};

const PrinterSettingsPage = ({ embedded = false, children = null }) => {
  const [feedback, setFeedback] = useState(null);
  const saveRef = useRef(null);
  const [settings, setSettings] = useState(() => ({
    printerWidth: readString("printerWidth", DEFAULTS.printerWidth),
    printerFeedCount: readString("pos_printer_feed_count", DEFAULTS.printerFeedCount),
    printLogoOnBill: readBool("printer_setting_printLogoOnBill", DEFAULTS.printLogoOnBill),
    printUpiQrOnBill: readBool("printer_setting_printUpiQrOnBill", DEFAULTS.printUpiQrOnBill),
    alwaysPrintUpiQr: readBool("printer_setting_alwaysPrintUpiQr", DEFAULTS.alwaysPrintUpiQr),
    printTokenNo: readBool("printer_setting_printTokenNo", DEFAULTS.printTokenNo),
    printGoogleReviewQr: readBool("printer_setting_printGoogleReviewQr", DEFAULTS.printGoogleReviewQr),
    boldRestaurantName: readBool("printer_setting_boldRestaurantName", DEFAULTS.boldRestaurantName),
    boldGrandTotal: readBool("printer_setting_boldGrandTotal", DEFAULTS.boldGrandTotal),
    logoSize: readString("printer_setting_logoSize", DEFAULTS.logoSize),
    upiQrSize: readString("printer_setting_upiQrSize", DEFAULTS.upiQrSize),
    businessNameSize: readString("printer_setting_businessNameSize", DEFAULTS.businessNameSize),
    totalAmountSize: readString("printer_setting_totalAmountSize", DEFAULTS.totalAmountSize),
    savingAmountSize: readString("printer_setting_savingAmountSize", DEFAULTS.savingAmountSize),
    googleReviewQrSize: readString("printer_setting_googleReviewQrSize", DEFAULTS.googleReviewQrSize),
    gstPrintFormat: readString("printer_setting_gstPrintFormat", DEFAULTS.gstPrintFormat),
    restaurantNameSize: readString("pos_restaurant_name_size", DEFAULTS.restaurantNameSize),
    customerNameSize: readString("pos_customer_name_size", DEFAULTS.customerNameSize),
  }));

  const setValue = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));
  const showFeedback = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback(null), 2200);
  };

  const save = () => {
    persistPrinterSettings(settings);
    toast.success("Printer settings saved");
    showFeedback("success", "Printer settings saved");
  };
  saveRef.current = save;

  useEffect(() => {
    persistPrinterSettings(settings);
  }, [settings]);

  const reset = () => {
    setSettings(DEFAULTS);
    Object.entries(DEFAULTS).forEach(([key, value]) => {
      const map = {
        printerWidth: ["printerWidth", "printer_width"],
        printerFeedCount: ["pos_printer_feed_count"],
        printLogoOnBill: ["printer_setting_printLogoOnBill"],
        printUpiQrOnBill: ["printer_setting_printUpiQrOnBill"],
        alwaysPrintUpiQr: ["printer_setting_alwaysPrintUpiQr"],
        printTokenNo: ["printer_setting_printTokenNo"],
        printGoogleReviewQr: ["printer_setting_printGoogleReviewQr"],
        boldRestaurantName: ["printer_setting_boldRestaurantName"],
        boldGrandTotal: ["printer_setting_boldGrandTotal"],
        logoSize: ["printer_setting_logoSize"],
        upiQrSize: ["printer_setting_upiQrSize"],
        businessNameSize: ["printer_setting_businessNameSize"],
        totalAmountSize: ["printer_setting_totalAmountSize"],
        savingAmountSize: ["printer_setting_savingAmountSize"],
        googleReviewQrSize: ["printer_setting_googleReviewQrSize"],
        gstPrintFormat: ["printer_setting_gstPrintFormat"],
        restaurantNameSize: ["pos_restaurant_name_size"],
        customerNameSize: ["pos_customer_name_size"],
      };
      (map[key] || []).forEach((storageKey) => localStorage.setItem(storageKey, String(value)));
    });
    toast.success("Printer settings reset");
    showFeedback("success", "Printer settings reset");
  };

  const toggles = useMemo(
    () => [
      ["Print logo on bill", "printLogoOnBill", "Restaurant logo on top of printed bill"],
      ["Print UPI QR for UPI payments", "printUpiQrOnBill", "Print the payment QR when payment mode is UPI"],
      ["Also print UPI QR for Cash/Card", "alwaysPrintUpiQr", "Requires the UPI QR option above to be enabled"],
      ["Print token number", "printTokenNo", "Include KOT/order token on bill"],
      ["Bold Restaurant Name", "boldRestaurantName", "Make restaurant name extra bold on bill"],
      ["Bold Grand Total", "boldGrandTotal", "Make total amount extra bold on bill"],
    ],
    []
  );

  useEffect(() => {
    const onShortcut = (event) => {
      if (event?.detail?.id !== "printer.save") return;
      event.preventDefault();
      saveRef.current?.();
    };

    window.addEventListener(APP_SHORTCUT_EVENT, onShortcut);
    return () => window.removeEventListener(APP_SHORTCUT_EVENT, onShortcut);
  }, []);

  return (
    <Page $embedded={embedded}>
      {!embedded && (
        <Header>
          <h1>Printer / POS Settings</h1>
          <p>Control thermal printing, billing, and POS preferences.</p>
        </Header>
      )}

      {children}

      <Card>
        <h3>Thermal Paper Width</h3>
        <Row>
          <Select
            value={settings.printerWidth}
            onChange={(e) => setValue("printerWidth", e.target.value)}
          >
            <option value="2in">2 inch</option>
            <option value="3in">3 inch</option>
            <option value="4in">4 inch</option>
            <option value="a4">A4</option>
          </Select>
        </Row>
        <Field>
          <label>Blank lines after print</label>
          <Select
            value={settings.printerFeedCount}
            onChange={(e) => setValue("printerFeedCount", e.target.value)}
          >
            {Array.from({ length: 13 }, (_, index) => (
              <option key={index} value={String(index)}>{index}</option>
            ))}
          </Select>
        </Field>
      </Card>

      <Card>
        <h3>Bill Options</h3>
        {toggles.map(([label, key, hint]) => (
          <ToggleRow
            key={key}
            $active={settings[key]}
            $disabled={key === "alwaysPrintUpiQr" && !settings.printUpiQrOnBill}
          >
            <ToggleText>
              <strong>{label}</strong>
              <small>{hint}</small>
            </ToggleText>
            <Switch>
              <HiddenCheckbox
                type="checkbox"
                checked={settings[key]}
                disabled={key === "alwaysPrintUpiQr" && !settings.printUpiQrOnBill}
                onChange={(e) => setValue(key, e.target.checked)}
              />
              <Slider $checked={settings[key]} />
            </Switch>
          </ToggleRow>
        ))}
      </Card>

      <Card>
        <h3>Size Preferences</h3>
        <Field>
          <label>Logo size</label>
          <Select value={settings.logoSize} onChange={(e) => setValue("logoSize", e.target.value)}>
            {SIZE_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </Field>
        <Field>
          <label>UPI QR size</label>
          <Select value={settings.upiQrSize} onChange={(e) => setValue("upiQrSize", e.target.value)}>
            {SIZE_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </Field>
        <Field>
          <label>Restaurant name size</label>
          <Select value={settings.restaurantNameSize} onChange={(e) => setValue("restaurantNameSize", e.target.value)}>
            <option value="small">small</option>
            <option value="large">large</option>
          </Select>
        </Field>
        <Field>
          <label>Customer name size</label>
          <Select value={settings.customerNameSize} onChange={(e) => setValue("customerNameSize", e.target.value)}>
            <option value="small">small</option>
            <option value="large">large</option>
          </Select>
        </Field>
        <Field>
          <label>{TAX_LABEL} print format</label>
          <Select value={settings.gstPrintFormat} onChange={(e) => setValue("gstPrintFormat", e.target.value)}>
            {GST_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </Field>
      </Card>

      <Actions>
        {feedback ? (
          <InlineNotice $type={feedback.type}>{feedback.message}</InlineNotice>
        ) : null}
        {!feedback ? (
          <AutoSaveStatus><span>✓</span><div><strong>All changes saved</strong><small>Auto-save is on</small></div></AutoSaveStatus>
        ) : null}
        <Btn type="button" onClick={reset}>Reset</Btn>
      </Actions>
    </Page>
  );
};

export default PrinterSettingsPage;

const Page = styled.div`
  min-height: ${({ $embedded }) => ($embedded ? "auto" : "100vh")};
  padding: ${({ $embedded }) => ($embedded ? "0" : "24px")};
  background: ${({ $embedded }) => ($embedded ? "transparent" : "#f4f7fb")};
  display: grid;
  align-content:start;gap: 16px;
  @media(max-width:650px){padding:14px;}
`;

const Header = styled.div`
  background:linear-gradient(135deg,var(--app-primary),var(--app-primary-hover));color:#fff;
  border-radius:22px;padding:27px 30px;box-shadow:0 14px 30px rgba(var(--app-primary-rgb), .17);
  h1 { margin: 0; font-size: 2rem; }
  p { margin: 6px 0 0; color: #dff3ff; }
  @media(max-width:600px){padding:22px 18px;}
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;padding:18px;box-shadow:0 6px 18px rgba(15,23,42,.04);
  h3 { margin: 0 0 14px;padding-bottom:12px;border-bottom:1px solid #e8eef4;font-size:1.05rem; }
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const ToggleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
  padding: 12px 14px;
  border: 1px solid ${({ $active }) => ($active ? "rgba(var(--app-primary-rgb), .34)" : "#e2e8f0")};
  border-radius: 13px;
  background: ${({ $active }) =>
    $active ? "linear-gradient(180deg,var(--app-primary-soft),#ffffff)" : "#ffffff"};
  margin-bottom: 10px;
`;

const ToggleText = styled.div`
  display: grid;
  gap: 3px;
  strong {
    color: #0f172a;
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.2;
  }
  small {
    color: #64748b;
    font-size: 12px;
    line-height: 1.25;
  }
`;

const Switch = styled.label`
  position: relative;
  width: 50px;
  height: 28px;
  display: inline-block;
  flex-shrink: 0;
  cursor: pointer;
`;

const HiddenCheckbox = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
`;

const Slider = styled.span`
  position: absolute;
  inset: 0;
  border-radius: 999px;
  background: ${({ $checked }) => ($checked ? "var(--app-primary)" : "#cbd5e1")};
  transition: background 0.2s ease;

  &::before {
    content: "";
    position: absolute;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #fff;
    top: 3px;
    left: ${({ $checked }) => ($checked ? "25px" : "3px")};
    box-shadow: 0 3px 8px rgba(15, 23, 42, 0.26);
    transition: left 0.2s ease;
  }
`;

const Field = styled.div`
  display: grid;
  gap: 6px;
  margin-bottom: 10px;
  label { font-size: 13px; color: #334155; }
`;

const Select = styled.select`
  height: 43px;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  padding: 0 10px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  position:sticky;bottom:10px;background:rgba(244,247,251,.92);padding:10px;border-radius:14px;backdrop-filter:blur(8px);
`;

const InlineNotice = styled.div`
  margin-right: auto;
  padding: 8px 12px;
  border-radius: 10px;
  font-weight: 800;
  border: 1px solid ${({ $type }) => ($type === "success" ? "#86efac" : "#fecaca")};
  color: ${({ $type }) => ($type === "success" ? "#166534" : "#991b1b")};
  background: ${({ $type }) => ($type === "success" ? "#f0fdf4" : "#fef2f2")};
`;
const AutoSaveStatus = styled.div`
  margin-right: auto;
  display: flex;
  align-items: center;
  gap: 9px;
  color: #166534;
  span {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: #dcfce7;
    font-weight: 900;
  }
  div { display: grid; gap: 1px; }
  strong { font-size: 13px; }
  small { color: #64748b; font-size: 11px; }
`;

const Btn = styled.button`
  border: 1px solid ${({ $primary }) => ($primary ? "var(--app-primary)" : "#cbd5e1")};
  background: ${({ $primary }) => ($primary ? "var(--app-primary)" : "#fff")};
  color: ${({ $primary }) => ($primary ? "#fff" : "#0f172a")};
  border-radius: 10px;
  height: 40px;
  padding: 0 14px;
  font-weight: 700;
  cursor: pointer;
`;
